import { BaseCardWrapper } from '@healthhub/react-ui';
import { Skeleton, Typography } from 'antd';
import type { KeyboardEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { NoPaddingFilledInfoIcon, OutlinedRightArrowIcon } from '@shared/assets-icon';
import { RelatedPeopleStatus } from '@shared/constants/constants';
import type { OnClickProfileParams } from '@shared/hooks/useFamilyHealth';
import type {
  RelatedPersonProfile,
  RelatedPersonTypeType,
} from '@shared/services/caregivers/types/getRelatedPeople.types';
import { RelatedPersonType } from '@shared/services/caregivers/types/getRelatedPeople.types';
import { getProfilePictureS3ImageDisplay } from '@shared/utils/profile';

const { Text } = Typography;

interface FamilyHealthProfileListingProps {
  data?: RelatedPersonProfile[];
  defaultProfilePicDisplay: string;
  description: string;
  icon: ReactNode;
  id?: RelatedPersonTypeType;
  isClickable: (profile: RelatedPersonProfile) => boolean;
  isLoading: boolean;
  onClickAddNew: () => void;
  onClickProfile: (params: OnClickProfileParams) => void;
  title: string;
}

const FamilyHealthProfileListing = ({
  data,
  description,
  icon,
  id,
  isClickable,
  isLoading,
  onClickAddNew,
  onClickProfile,
  title,
  defaultProfilePicDisplay,
}: FamilyHealthProfileListingProps) => {
  const { t } = useTranslation('home');

  const onKeyDownAddNew = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      onClickAddNew();
    }
  };

  const renderLabel = (record: RelatedPersonProfile) => {
    if (
      record.Status === RelatedPeopleStatus.REMOVED_BY_CAREGIVER &&
      record.FlowType === RelatedPersonType.CAREGIVERS
    ) {
      return t('removed');
    }

    if (record.FlowType === RelatedPersonType.FAMILY_AND_FRIEND) {
      return t('pendingRecipientAction');
    }

    return t('actionRequired');
  };

  return (
    <div className="flex flex-col gap-1 lg:gap-4">
      <Text className="hidden text-base font-semibold lg:block">{title}</Text>
      {isLoading ? (
        <Skeleton.Button active className="mt-1 h-64 w-full rounded-lg" />
      ) : (
        <>
          <div onClick={onClickAddNew} onKeyDown={onKeyDownAddNew} role="button" tabIndex={0}>
            <BaseCardWrapper className="flex gap-4 p-2 items-center">
              {icon}
              <Text className="self-center text-base font-semibold text-primary-blue-grey">{description}</Text>
            </BaseCardWrapper>
          </div>
          {(data?.length ?? 0) > 0 && (
            <BaseCardWrapper className="flex flex-col gap-4 p-4 shadow-portal-card lg:px-4 lg:py-6">
              <div className="flex flex-col gap-1 lg:gap-3">
                {data?.map((record) => {
                  const canClick = isClickable(record);
                  const isRemovedByCaregiver = record.Status === RelatedPeopleStatus.REMOVED_BY_CAREGIVER;

                  const onClickProfileItem = () => {
                    if (canClick) {
                      onClickProfile({
                        user: record,
                      });
                    }
                  };

                  const onKeyDownProfileItem = (e: KeyboardEvent<HTMLDivElement>) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      onClickProfileItem();
                    }
                  };

                  return (
                    <div
                      className="flex gap-2 p-2 text-start"
                      key={record.Prn}
                      onClick={onClickProfileItem}
                      onKeyDown={onKeyDownProfileItem}
                      role="button"
                      tabIndex={0}
                    >
                      <img
                        alt=""
                        className="gbomit size-13 rounded-xl object-cover"
                        data-testid="profile-picture"
                        onError={(e) => {
                          e.currentTarget.src = defaultProfilePicDisplay;
                        }}
                        src={getProfilePictureS3ImageDisplay(
                          record.Photo?.Data ?? defaultProfilePicDisplay,
                          null,
                          null
                        )}
                      />
                      <div className="flex flex-1 flex-col gap-0.5">
                        <Text className="gbmask text-base font-semibold text-primary-green-4">
                          {record.DisplayName}
                        </Text>
                        {record.Status !== RelatedPeopleStatus.PENDING ? (
                          <>
                            {!!record.RelationShip && (
                              <Text className="gbmask text-sm text-functional-iron">
                                {t(record.RelationShip.toLowerCase())}
                              </Text>
                            )}

                            {!record?.IsMyChartSync &&
                              !isRemovedByCaregiver &&
                              (record.FlowType === RelatedPersonType.FAMILY_AND_FRIEND ||
                                record.FlowType === RelatedPersonType.CAREGIVERS) && (
                                <Text className="gbmask flex self-start items-center gap-2 rounded-3xl bg-primary-yellow px-4 py-1 text-xs font-semibold text-secondary-orange-4">
                                  <NoPaddingFilledInfoIcon className="size-2.5 shrink-0 [&_path]:stroke-secondary-orange-4 [&_circle:last-child]:fill-secondary-orange-4 [&_circle:first-child]:stroke-secondary-orange-4" />
                                  {renderLabel(record)}
                                </Text>
                              )}

                            {!!isRemovedByCaregiver && record.FlowType === RelatedPersonType.CAREGIVERS && (
                              <Text className="gbmask flex self-start rounded-3xl  bg-functional-stone px-4 py-1 text-xs font-semibold text-functional-iron">
                                {renderLabel(record)}
                              </Text>
                            )}
                          </>
                        ) : (
                          <Text className="gbmask self-start rounded-3xl bg-primary-yellow px-4 py-1 text-xs font-semibold text-secondary-orange-4">
                            {id === record.FlowType ? t('pendingApproval') : t('actionRequired')}
                          </Text>
                        )}
                      </div>
                      {!!canClick && (
                        <OutlinedRightArrowIcon className="size-6px self-start [&_path]:stroke-primary-green-4" />
                      )}
                    </div>
                  );
                })}
              </div>
            </BaseCardWrapper>
          )}
        </>
      )}
    </div>
  );
};

export default FamilyHealthProfileListing;
